/* ===========================================================
   editor/images.js — adding pictures

   Shrinks a picture in the browser first (longest side 2000 px,
   JPEG/WebP ~85%), so phone photos upload fast and fit the 2 MB limit,
   then uploads it to the doc's storage (R2) and inserts it.
   =========================================================== */

import { api } from '../lib/api.js';
import { LIMITS } from '../../shared/protocol.js';

const MAX_SIDE = 2000;

async function shrink(file) {
  if (file.type === 'image/gif' && file.size <= LIMITS.imageBytes) return file;   // keep animations
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) throw new Error('That file isn\'t a picture I can read. Try a PNG or JPEG.');
  const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
  if (scale === 1 && file.size <= LIMITS.imageBytes * 0.6 && /png|jpeg|webp/.test(file.type)) return file;
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
  const keepAlpha = file.type === 'image/png';
  for (const q of [0.88, 0.8, 0.7, 0.55]) {
    const blob = await new Promise(r => canvas.toBlob(r, keepAlpha ? 'image/webp' : 'image/jpeg', q));
    if (blob && blob.size <= LIMITS.imageBytes) return blob;
  }
  throw new Error('That picture is too big, even after shrinking it.');
}

/** Upload and return the address to use in the doc. */
export async function uploadImage(docId, file) {
  if (!navigator.onLine) throw new Error('Adding pictures needs the internet. Try again when you\'re back online.');
  const blob = await shrink(file);
  const r = await api(`/api/files?doc=${encodeURIComponent(docId)}`, { method: 'POST', raw: blob, type: blob.type });
  return r.url;
}

export function pickImage() {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp,image/gif';
    input.onchange = () => resolve(input.files?.[0] || null);
    input.click();
  });
}
